import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { ActionDialog, Button, Download, EmailPreview, EmptyState, Modal, Paperclip, Tabs, Tags } from "../../ui";
import { integrationLabel } from "../../../utils/integrations";
import { createSendRequestsAPI, previewSendToAppAPI } from "../../../utils/api/menuAppSend";
import { sendRequestEmailsAPI } from "../../../utils/api/requestEmails";
import { requestsText } from "../../ludDashboard/Requests/unified/unifiedText";
import { menuErrorMessage } from "../menuRole";
import { sendToAppText } from "./sendToAppText";
import { sendToAppPreviewText } from "./sendToAppPreviewText";
import "./SendToApp.css";

// "Send to App": preview -> (Send Now | Save as Draft) -> confirmation -> send.
//
//   1. the server builds the e-mail and the Excel of every chosen app (nothing is saved or sent);
//   2. Save as Draft  -> one Dashboard Request (draft) per app is created, nothing is sent;
//      Send Now       -> the same requests are created, then a confirmation shows the app, the
//                        recipients and the attachments, and only its confirmation sends;
//   3. the request status changes on the server only after the mail provider accepted the
//      message; a failure keeps the request (failed + error) in Requests.
// Repeated clicks are ignored while a step runs and every send quotes a stable idempotency key.

const newKey = () => (typeof crypto !== "undefined" && crypto.randomUUID ? crypto.randomUUID() : `k-${Date.now()}-${Math.random().toString(36).slice(2)}`);
const formatSize = (bytes) => (bytes >= 1048576 ? `${(bytes / 1048576).toFixed(1)} MB` : bytes >= 1024 ? `${Math.round(bytes / 1024)} KB` : `${bytes} B`);
const pickName = (name, lang) => (name ? (lang === "ar" ? name.ar || name.en : name.en || name.ar) : "");

function downloadBase64(file) {
  const bytes = Uint8Array.from(atob(file.contentBase64), (char) => char.charCodeAt(0));
  const url = URL.createObjectURL(new Blob([bytes], { type: file.contentType }));
  const link = document.createElement("a");
  link.href = url;
  link.download = file.filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function FilePreview({ file, p, lang }) {
  const [open, setOpen] = useState(false);
  const rows = file.preview?.rows || [];
  return (
    <section className="send-preview-file" aria-label={p.attachment}>
      <h4>{p.attachment}</h4>
      <div className="send-preview-file-head">
        <span className="send-preview-file-name">
          <Paperclip size={14} aria-hidden="true" /> <span dir="auto">{file.filename}</span> <small>({formatSize(file.size)})</small>
        </span>
        <span className="send-preview-file-actions">
          <Button type="button" size="sm" variant="primary" appearance="soft" icon={<Download size={14} />} onClick={() => downloadBase64(file)}>
            {p.download}
          </Button>
          <Button type="button" size="sm" variant="neutral" appearance="soft" aria-expanded={open} onClick={() => setOpen((value) => !value)}>
            {open ? p.hideFile : p.previewFile}
          </Button>
        </span>
      </div>
      {open && (
        <div className="send-preview-table-wrap" tabIndex={0} role="region" aria-label={p.previewFile}>
          <table className="send-preview-table">
            <thead>
              <tr>
                <th>{p.columns.section}</th>
                <th>{p.columns.item}</th>
                <th>{p.columns.price}</th>
                <th>{p.columns.additions}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={index}>
                  <td dir="auto">{pickName(row.section, lang)}</td>
                  <td dir="auto">{pickName(row.name, lang)}</td>
                  <td>{row.price}</td>
                  <td>{row.additions}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {file.preview?.total > rows.length && <p className="send-preview-more">{p.moreRows(file.preview.total - rows.length)}</p>}
        </div>
      )}
    </section>
  );
}

export default function SendToAppPreviewModal({ menuId, apps, itemIds, action, onClose, onDraftSaved, onSent, onStale }) {
  const { lang } = useContext(Context);
  const p = sendToAppPreviewText(lang);
  const st = sendToAppText(lang);
  const mailText = requestsText(lang).email;
  const token = localStorage.getItem("auth-token");
  const [phase, setPhase] = useState("loading"); // loading | ready | error | confirm | result
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [tab, setTab] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState([]);
  const [results, setResults] = useState([]);
  const keys = useRef(new Map());
  const working = useRef(false); // synchronous guard against double clicks

  const describe = useCallback(
    (failure) => p.errors[failure?.code] || mailText.errors?.[failure?.code] || st.errors?.[failure?.code] || menuErrorMessage(failure, lang) || failure?.message || "",
    [p, mailText, st, lang],
  );

  const load = useCallback(() => {
    let alive = true;
    setPhase("loading");
    setLoadError("");
    previewSendToAppAPI(menuId, { apps, itemIds, action })
      .then((data) => {
        if (!alive) return;
        setPreview(data);
        setTab(data.emails[0]?.app.key || "");
        setPhase("ready");
      })
      .catch((failure) => {
        if (!alive) return;
        // an item became hidden / incomplete meanwhile: the Menu page re-reads what can be sent
        if (failure?.code === "ITEMS_NOT_ELIGIBLE") onStale?.(failure);
        setLoadError(describe(failure));
        setPhase("error");
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuId, apps.join(","), itemIds.join(","), action]);
  useEffect(() => load(), [load]);

  const run = async (step) => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    setError("");
    try {
      await step();
    } catch (failure) {
      if (failure?.code === "ITEMS_NOT_ELIGIBLE") onStale?.(failure);
      setError(describe(failure));
    } finally {
      working.current = false;
      setBusy(false);
    }
  };

  const emails = preview?.emails || [];
  const nameOf = (entry) => integrationLabel(entry.app.key, lang) || pickName(entry.app, lang);
  const actionName = p.action[action] || action;
  const current = emails.find((entry) => entry.app.key === tab) || emails[0];

  const createRequests = () => createSendRequestsAPI(menuId, { apps, itemIds, action });

  const saveDraft = () =>
    run(async () => {
      const requests = await createRequests();
      onDraftSaved?.({ requests, message: `${p.draftSaved(requests.length)}${requests.some((entry) => entry.existing) ? ` ${p.draftExisting}` : ""}` });
    });

  const sendNow = () =>
    run(async () => {
      // the request exists from here on: whatever happens next, it is visible (and recoverable) in Requests
      const requests = await createRequests();
      setCreated(requests);
      setPhase("confirm");
    });

  const confirmSend = async () => {
    if (working.current) return;
    working.current = true;
    setBusy(true);
    const outcome = [];
    for (const request of created) {
      if (!request.draftHash) {
        outcome.push({ request, state: "draft", message: p.notSentOne });
        continue;
      }
      if (!keys.current.has(request.id)) keys.current.set(request.id, newKey());
      try {
        const response = await sendRequestEmailsAPI(token, "dashboard", request.id, { purpose: "menu.appEmail", lang, draftHash: request.draftHash, idempotencyKey: keys.current.get(request.id) });
        const email = response.emails?.[0];
        outcome.push({ request, state: email?.status === "sent" ? "sent" : "failed", message: email?.status === "sent" ? p.sentOne : email?.lastError || p.failedOne });
      } catch (failure) {
        outcome.push({ request, state: "failed", message: describe(failure) || p.failedOne });
      }
    }
    working.current = false;
    setBusy(false);
    setResults(outcome);
    setPhase("result");
    onSent?.({ results: outcome });
  };

  const labelFor = (request) => integrationLabel(request.app, lang) || pickName(request.appLabel, lang) || request.app;

  // ---- confirmation (after the requests were created) ------------------------------------------------------------
  if (phase === "confirm") {
    const byApp = new Map(emails.map((entry) => [entry.app.key, entry]));
    return (
      <ActionDialog
        t={requestsText(lang)}
        title={p.confirmTitle}
        description={p.confirmText}
        confirmLabel={p.confirmSend}
        labels={{ cancelDialog: p.confirmBack }}
        onClose={() => {
          if (working.current) return;
          onDraftSaved?.({ requests: created, message: p.draftSaved(created.length) });
        }}
        onConfirm={confirmSend}
      >
        <ul className="send-confirm-list">
          {created.map((request) => {
            const entry = byApp.get(request.app);
            return (
              <li key={request.id}>
                <strong>{labelFor(request)}</strong>
                <dl>
                  <dt>{p.confirmTo}</dt>
                  <dd dir="ltr">{(entry?.to || []).join(", ")}</dd>
                  {entry?.cc?.length > 0 && (
                    <>
                      <dt>{p.confirmCc}</dt>
                      <dd dir="ltr">{entry.cc.join(", ")}</dd>
                    </>
                  )}
                  <dt>{p.confirmFiles}</dt>
                  <dd dir="auto">{entry?.attachment ? `${entry.attachment.filename} (${formatSize(entry.attachment.size)})` : "—"}</dd>
                  <dt>{p.confirmItems}</dt>
                  <dd>{`${actionName}: ${p.items(preview.itemCount)}`}</dd>
                  <dt>{p.reference}</dt>
                  <dd dir="ltr">{request.requestNumber}</dd>
                </dl>
              </li>
            );
          })}
        </ul>
      </ActionDialog>
    );
  }

  // ---- result -------------------------------------------------------------------------------------------------------------
  if (phase === "result") {
    const allSent = results.every((entry) => entry.state === "sent");
    return (
      <Modal title={p.resultTitle} size="md" onClose={onClose} closeLabel={p.close} footer={<Button variant="primary" appearance="filled" onClick={onClose}>{p.close}</Button>}>
        <div className="send-result">
          <p role="status">{allSent ? p.allSent : p.someFailed}</p>
          <ul>
            {results.map(({ request, state, message }) => (
              <li key={request.id}>
                <strong>{labelFor(request)}</strong>
                <Tags size="sm" variant={state === "sent" ? "success" : state === "failed" ? "danger" : "neutral"} appearance="soft">
                  {mailText.status[state === "sent" ? "sent" : state === "failed" ? "failed" : "none"]}
                </Tags>
                <p className={state === "failed" ? "send-result-error" : undefined} dir="auto">{message}</p>
                <small dir="ltr">{request.requestNumber}</small>
              </li>
            ))}
          </ul>
        </div>
      </Modal>
    );
  }

  // ---- review ---------------------------------------------------------------------------------------------------------------
  const tabs = emails.map((entry) => ({ key: entry.app.key, label: nameOf(entry) }));
  return (
    <Modal
      title={p.title}
      description={phase === "ready" ? p.description : undefined}
      size="lg"
      busy={busy}
      onClose={onClose}
      closeLabel={p.close}
      className="send-preview-modal"
      footer={
        <>
          <Button variant="primary" appearance="filled" disabled={busy || phase !== "ready" || !preview?.canSend} loading={busy} onClick={sendNow}>
            {p.sendNow}
          </Button>
          <Button variant="primary" appearance="soft" disabled={busy || phase !== "ready" || !preview?.canSend} onClick={saveDraft}>
            {p.saveDraft}
          </Button>
          <Button variant="neutral" appearance="soft" disabled={busy} onClick={onClose}>
            {p.cancel}
          </Button>
        </>
      }
    >
      {phase === "loading" && <EmptyState variant="loading" compact>{p.loading}</EmptyState>}
      {phase === "error" && (
        <EmptyState variant="error" compact title={p.loadFailed} action={<Button size="sm" onClick={load}>{p.retry}</Button>}>
          {loadError}
        </EmptyState>
      )}
      {phase === "ready" && preview && (
        <div className="send-preview-body">
          <p className="send-preview-summary">
            <strong>{actionName}</strong> · {p.items(preview.itemCount)}
          </p>
          <section className="send-preview-recipients" aria-label={p.recipients}>
            <h4>{p.recipients}</h4>
            <ul>
              {emails.map((entry) => (
                <li key={entry.app.key}>
                  <Tags size="sm" variant={entry.problem ? "danger" : "primary"} appearance="soft">
                    {nameOf(entry)}
                  </Tags>
                  <span className="send-preview-addresses" dir="ltr">
                    {entry.problem ? "—" : `${p.confirmTo}: ${entry.to.join(", ")}${entry.cc?.length ? ` · ${p.confirmCc}: ${entry.cc.join(", ")}` : ""}`}
                  </span>
                </li>
              ))}
            </ul>
          </section>
          {emails.length > 1 && <Tabs tabs={tabs} value={current?.app.key} onChange={setTab} label={p.tabs} idPrefix="send-preview" rtl={lang === "ar"} />}
          {current && (
            <div role="tabpanel" id={`send-preview-panel-${current.app.key}`} aria-labelledby={`send-preview-tab-${current.app.key}`}>
              {current.problem ? (
                <div className="send-preview-problem" role="alert">
                  <strong>{p.problemTitle}: {nameOf(current)}</strong>
                  <p>{st.errors?.[current.problem.code] || current.problem.message}</p>
                </div>
              ) : (
                <>
                  <EmailPreview message={{ ...current, attachments: current.attachment ? [{ filename: current.attachment.filename, size: current.attachment.size, sha256: "preview" }] : [] }} labels={mailText} lang={lang} />
                  {current.attachment && <FilePreview file={current.attachment} p={p} lang={lang} />}
                </>
              )}
            </div>
          )}
          {error && <p className="send-preview-error" role="alert">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
